import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, HandHeart, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const METHODS = [
  { id: "stripe", label: "Bank (Stripe)" },
  { id: "venmo", label: "Venmo" },
  { id: "cashapp", label: "Cash App" },
];

export default function BecomeRecipient() {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [story, setStory] = useState("");
  const [goal, setGoal] = useState("");
  const [phone, setPhone] = useState("");
  const [venmo, setVenmo] = useState("");
  const [methods, setMethods] = useState(["stripe"]);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setMethods((m) => (m.includes(id) ? m.filter((x) => x !== id) : [...m, id]));

  const submit = async () => {
    if (!phone.trim()) {
      toast.error("A phone number is required.");
      return;
    }
    if (methods.length === 0) {
      toast.error("Pick at least one payout method.");
      return;
    }
    setSaving(true);
    try {
      await api.post("/recipients/apply", {
        story: story.trim(),
        goal_amount: goal ? Number(goal) : null,
        phone_number: phone.trim(),
        payout_methods: methods,
        venmo_handle: venmo.trim() || null,
      });
      await refreshUser();
      toast.success("You're now a recipient! 🙌");
      if (methods.includes("stripe")) navigate("/onboard-bank");
      else navigate("/profile");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit application.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-lg mx-auto px-4 py-5 pb-28">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm font-bold text-ink-tertiary mb-4">
        <ArrowLeft size={18} /> Back
      </button>

      <div className="flex items-center gap-3 mb-2">
        <div className="w-12 h-12 rounded-pill grid place-items-center text-white" style={{ background: "linear-gradient(135deg,#FFD93D,#FF8A3D)" }}>
          <HandHeart size={24} />
        </div>
        <h1 className="text-2xl font-black">Become a recipient</h1>
      </div>
      <p className="text-sm text-ink-tertiary font-semibold mb-6">Share your story so neighbors can bless you. Your Google photo is used as your avatar.</p>

      <div className="space-y-4">
        <div>
          <label className="text-sm font-black">Your story</label>
          <Textarea data-testid="recipient-story" value={story} onChange={(e) => setStory(e.target.value)} placeholder="Tell people what you're going through and how a blessing would help…" className="mt-1 bg-surface-2 border-border min-h-28" />
        </div>
        <div>
          <label className="text-sm font-black">Goal amount (optional)</label>
          <Input data-testid="recipient-goal" type="number" value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="$" className="mt-1 bg-surface-2 border-border" />
        </div>
        <div>
          <label className="text-sm font-black">Phone number</label>
          <Input data-testid="recipient-phone" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="For verification only" className="mt-1 bg-surface-2 border-border" />
        </div>
        <div>
          <label className="text-sm font-black">Payout methods</label>
          <div className="mt-2 space-y-2">
            {METHODS.map((m) => (
              <label key={m.id} className="flex items-center gap-3 bg-surface-2 border border-border rounded-md px-4 py-3 cursor-pointer">
                <Checkbox data-testid={`method-${m.id}`} checked={methods.includes(m.id)} onCheckedChange={() => toggle(m.id)} />
                <span className="font-bold text-sm">{m.label}</span>
              </label>
            ))}
          </div>
        </div>
        {methods.includes("venmo") && (
          <div>
            <label className="text-sm font-black">Venmo handle</label>
            <Input data-testid="recipient-venmo" value={venmo} onChange={(e) => setVenmo(e.target.value)} placeholder="@your-handle" className="mt-1 bg-surface-2 border-border" />
          </div>
        )}
      </div>

      <Button data-testid="recipient-submit" disabled={saving} onClick={submit} className="w-full h-13 mt-6 h-12 rounded-pill font-black bg-brand-primary text-white">
        {saving ? <Loader2 className="animate-spin" size={20} /> : "Submit application"}
      </Button>
    </div>
  );
}
